import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import { Crest } from '../display/crest'
import { GoldDivider } from '../display/gold-divider'

/**
 * JPG Academy Error State
 *
 * A more elevated empty/error treatment for 404, access denied,
 * expired links, and general application failures.
 */

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
	code?: string
	eyebrow?: string
	title: string
	description?: string
	primaryAction?: React.ReactNode
	secondaryAction?: React.ReactNode
	details?: React.ReactNode
	showCrest?: boolean
	fullPage?: boolean
	compact?: boolean
}

export function ErrorState({
	code = '404',
	eyebrow = 'Page not found',
	title,
	description,
	primaryAction,
	secondaryAction,
	details,
	showCrest = true,
	fullPage = false,
	compact = false,
	className,
	...props
}: ErrorStateProps) {
	return (
		<section
			className={cn(
				'relative isolate overflow-hidden rounded-[28px] border border-gold/20 bg-[linear-gradient(180deg,rgba(245,240,232,0.98)_0%,rgba(255,255,255,0.95)_100%)] text-center shadow-[0_24px_80px_rgba(17,29,53,0.12)]',
				'corner-ornament',
				compact ? 'px-5 py-8' : 'px-6 py-10 md:px-10 md:py-14',
				fullPage && 'min-h-[70vh] flex items-center justify-center',
				className,
			)}
			{...props}
		>
			<div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top,rgba(196,151,42,0.16),transparent_68%)]" />
			<div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(27,42,74,0.02),transparent_42%,rgba(139,26,43,0.05)_100%)]" />

			<motion.div
				className="relative mx-auto flex w-full max-w-3xl flex-col items-center"
				initial={{ opacity: 0, y: 18 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
			>
				{showCrest ? (
					<div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-gold/25 bg-white/70 shadow-[0_10px_30px_rgba(27,42,74,0.1)] md:h-24 md:w-24">
						<Crest size={compact ? 'sm' : 'lg'} />
					</div>
				) : null}

				<div className="mb-3 font-ui text-[11px] font-semibold uppercase tracking-[0.28em] text-maroon/80">
					{eyebrow}
				</div>

				<div className="text-gradient-gold font-display text-5xl leading-none md:text-7xl">
					{code}
				</div>

				<GoldDivider className="my-5 w-40 max-w-full" />

				<h1 className={cn('max-w-2xl text-navy-900', compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl')}>
					{title}
				</h1>

				{description ? (
					<p className={cn('mt-4 max-w-2xl font-body text-navy/75', compact ? 'text-base' : 'text-lg md:text-xl')}>
						{description}
					</p>
				) : null}

				{(primaryAction || secondaryAction) ? (
					<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
						{primaryAction}
						{secondaryAction}
					</div>
				) : null}

				{details ? (
					<div className="mt-8 w-full max-w-2xl rounded-2xl border border-navy-100 bg-white/80 p-4 text-left shadow-[0_10px_30px_rgba(27,42,74,0.06)]">
						<div className="font-ui text-xs uppercase tracking-[0.2em] text-navy/45">Details</div>
						<div className="mt-2 font-body text-sm text-navy/75">{details}</div>
					</div>
				) : null}
			</motion.div>
		</section>
	)
}
